import React from 'react';

import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { GenderType } from '@/types/domain/child/valueObjects/gender';
import type { Control } from 'react-hook-form';

import DateFieldComp from '@/components/DateFieldComp';
import SelectFieldComp from '@/components/SelectFieldComp';
import TextFieldComp from '@/components/TextFieldComp';
import { Birthday } from '@/domain/child/service/entity/valueObjects/birthday';
import { ChildId } from '@/domain/child/service/entity/valueObjects/childId';
import { FamilyName } from '@/domain/child/service/entity/valueObjects/familyName';
import { FamilyNameKana } from '@/domain/child/service/entity/valueObjects/familyNameKana';
import { Gender } from '@/domain/child/service/entity/valueObjects/gender';
import { Name } from '@/domain/child/service/entity/valueObjects/name';
import { NameKana } from '@/domain/child/service/entity/valueObjects/nameKana';

/**
 * 子どもJSON入力
 */
export type ChildJsonInput = {
  /** 子どもID */
  childId: string,
  /** 名前 */
  name: string,
  /** 名前かな */
  nameKana: string,
  /** 苗字 */
  familyName: string,
  /** 苗字かな */
  familyNameKana: string,
  /** 性別 */
  gender: GenderType,
  /** 誕生日 */
  birthday: string,
};

/**
 * プロパティ
 */
export type ChildFormInputType = {
  /** コントロール */
  control: Control<ChildJsonInput, never>,
  /** エラー */
  errors: any,
  /** 子どもリスト */
  childList: ChildJson[],
}

const ChildFormInput: React.FC<ChildFormInputType> = ({
  control,
  errors,
  childList,
}) => (
  <div>
    <div style={{ margin: '10px 0' }}>
      <TextFieldComp
        type={ChildId.type}
        control={control}
        rules={{
          ...ChildId.validationRule,
          validate: (value: string) => {
            const result = childList?.find((v) => v.childId === value);
            if (result) {
              return 'このIDは既に使用されています';
            }
            return null;
          },
        }}
        name={ChildId.key}
        label={ChildId.label}
        placeholder={ChildId.placeholder}
        errors={errors}
      />
    </div>
    <div
      style={{
        marginTop: '10px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
      }}
    >
      <TextFieldComp
        type={FamilyName.type}
        control={control}
        rules={FamilyName.validationRule}
        name={FamilyName.key}
        label={FamilyName.label}
        placeholder={FamilyName.placeholder}
        errors={errors}
      />
      <TextFieldComp
        type={Name.type}
        control={control}
        rules={Name.validationRule}
        name={Name.key}
        label={Name.label}
        placeholder={Name.placeholder}
        errors={errors}
      />
      <TextFieldComp
        type={FamilyNameKana.type}
        control={control}
        rules={FamilyNameKana.validationRule}
        name={FamilyNameKana.key}
        label={FamilyNameKana.label}
        placeholder={FamilyNameKana.placeholder}
        errors={errors}
      />
      <TextFieldComp
        type={NameKana.type}
        control={control}
        rules={NameKana.validationRule}
        name={NameKana.key}
        label={NameKana.label}
        placeholder={NameKana.placeholder}
        errors={errors}
      />
      <DateFieldComp
        control={control}
        name={Birthday.key}
        label={Birthday.label}
        rules={Birthday.validationRule}
        errors={errors}
      />
      <SelectFieldComp
        control={control}
        name={Gender.key}
        label={Gender.label}
        rules={Gender.validationRule}
        errors={errors}
        items={Gender.list}
      />
    </div>
  </div>
);

export default ChildFormInput;
